import { type FormEvent, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { isProblemError } from '../../api/client';
import { getGetMeQueryKey, useVerifyMfa } from '../../api/generated/identity/identity';
import { LanguageSelector } from '../../app/LanguageSelector';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Field } from '../../design/primitives/Field';
import { useLogoutAction } from './useSession';
import styles from './auth.module.css';

export function MfaChallengePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');

  const verify = useVerifyMfa();
  const logout = useLogoutAction();

  // O formulario e `noValidate` — a bolha nativa vem no idioma do sistema
  // operacional, e nao no idioma escolhido na plataforma —, entao `required` e
  // `pattern` nao barram nada sozinhos. Sem esta guarda, Enter com o campo
  // vazio manda uma requisicao que so pode falhar e a tela responde "codigo
  // incorreto", que e mentira: o codigo nao estava errado, estava incompleto.
  // Pior, a tentativa vazia conta para a trava por tentativas do servidor e
  // aproxima do 429 quem nem chegou a digitar.
  const canSubmit = /^[0-9]{6}$/.test(code) && !verify.isPending;

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!canSubmit) return;
    verify.mutate(
      { data: { code } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
          void navigate('/', { replace: true });
        },
      },
    );
  };

  const errorMessage = ((): string | undefined => {
    if (!verify.isError) return undefined;
    if (!isProblemError(verify.error)) return t('error.unexpected');
    if (verify.error.status === 429) return t('mfa.tooManyAttempts');
    if (verify.error.status === 401 || verify.error.status === 400) return t('mfa.invalidCode');
    return t('error.unexpected');
  })();

  return (
    <main className={styles.screen}>
      <div className={styles.card}>
        <h1 className={styles.title}>{t('mfa.title')}</h1>
        <p className={styles.subtitle}>{t('mfa.description')}</p>

        {errorMessage ? <Alert tone="error">{errorMessage}</Alert> : null}

        <form onSubmit={handleSubmit} noValidate>
          <Field
            label={t('mfa.code')}
            hint={t('mfa.codeHint')}
            name="code"
            required
            value={code}
            onChange={(event) => setCode(event.target.value)}
            // Teclado numerico no celular e preenchimento do codigo pelo
            // sistema operacional: os dois vem de atributo, e nao de mascara.
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            pattern="[0-9]{6}"
          />
          <Button
            className={styles.submit}
            type="submit"
            variant="primary"
            loading={verify.isPending}
            disabled={!canSubmit}
          >
            {verify.isPending ? t('mfa.submitting') : t('mfa.submit')}
          </Button>
        </form>

        {/*
          A saida da tela de desafio.

          Quem troca de celular chega aqui com sessao valida, nao passa do
          codigo e — sem este botao — nao volta ao login nem encerra a sessao
          pela interface: a unica saida era apagar cookie na mao. O portao de
          MFA e montado no lugar do `AppShell`, entao o "Sair" do menu nao
          existe nesta tela; ele precisa existir aqui.

          O contrato nao tem operacao de recuperacao de segundo fator, e a tela
          nao inventa uma: o texto diz o caminho que existe de verdade, que e
          fora da plataforma.
        */}
        <p className={styles.subtitle}>{t('mfa.lockedOut')}</p>
        <Button onClick={() => logout.mutate()} loading={logout.isPending}>
          {t('nav.logout')}
        </Button>
        {logout.isError ? <Alert tone="error">{t('nav.logoutFailed')}</Alert> : null}

        <div className={styles.footer}>
          <LanguageSelector />
        </div>
      </div>
    </main>
  );
}
